import { Component, EventEmitter, OnInit, Output } from '@angular/core'
import { forkJoin } from 'rxjs'

import { WorklogService } from '../../data/services/worklog.service'
import { EpicWithStatus, LooseTimelineItem } from '../../data/interfaces/epic.interface'
import { Sprint } from '../../data/interfaces/worklog.interface'

/**
 * Los TIEMPOS del trabajo: cuánto tardó cada implementación, de que arrancó a que se entregó.
 *
 * Es un host delgado — la cabecera del sprint activo, que da el marco temporal, y debajo la línea
 * de tiempo. Todo el dibujo vive en `worklog-gantt`.
 *
 * **Por qué una sola vista.** Antes eran dos modos que se quedaban cortos por separado: "En curso"
 * solo miraba el sprint activo y solo lo ya arrancado, y "Entregas pactadas" contrastaba la fecha
 * comprometida contra la real — una medición que nunca tuvo con qué, porque el equipo casi nunca
 * pacta fecha (en 169 entregas, ninguna la traía). La pregunta que sí importa, *cuánto nos tardamos
 * en implementar esto*, solo tenía respuesta en otra pestaña y solo para épicas.
 *
 * Ahora los dos carriles van en el mismo eje: las épicas y el trabajo de una sola tarea o bug.
 */
@Component({
  selector: 'app-worklog-timeline',
  templateUrl: './worklog-timeline.component.html',
  styleUrls: ['./worklog-timeline.component.scss'],
})
export class WorklogTimelineComponent implements OnInit {
  @Output() openItem = new EventEmitter<string>()
  /**
   * El Gantt emitía `select` al hacer clic en una épica desde que se mudó aquí, y en esta vista
   * nadie lo escuchaba: la fila se veía pulsable y no pasaba nada. Sube al contenedor, que es
   * quien tiene los diálogos.
   */
  @Output() openEpic = new EventEmitter<EpicWithStatus>()

  sprint: Sprint | null = null
  epics: EpicWithStatus[] = []
  loose: LooseTimelineItem[] = []
  loading = true

  constructor(private worklog: WorklogService) {}

  ngOnInit(): void {
    this.reload()
  }

  reload(): void {
    this.loading = true
    //* Las tres juntas: el Gantt reconstruye sus filas en cada cambio de entrada, así que entregar
    //* los dos carriles a la vez le evita una reconstrucción intermedia con medio eje.
    forkJoin({
      board: this.worklog.getBoard(),
      epics: this.worklog.getEpics(),
      loose: this.worklog.getLooseTimeline(),
    }).subscribe(({ board, epics, loose }) => {
      this.sprint = board?.sprint ?? null
      this.epics = epics
      this.loose = loose
      this.loading = false
    })
  }
}
